import { Autocomplete, DatePicker, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Box } from '@mui/material'
import { BooleanIcon } from 'components/core-ui/BooleanIcon'
import { BooleanIconText } from 'components/core-ui/BooleanIconText'
import { MultivalueCell } from 'components/core-ui/MultivalueCell'
import { ApproveFileWhite, DeleteFileRed, FileBlack } from 'icons'
import { DailyReportTableItem, ReportUploadStatus } from '../interfaces'
import { MetiReportsCell } from '../common/MetiReportsModal'

const reportUploadStatusLabels: Record<NonNullable<ReportUploadStatus>, string> = {
  MANUAL_UPLOAD: 'Manual',
  AUTOMATIC_UPLOAD: 'Automat',
  MISSING_UPLOAD: 'Nefinalizat'
}

const getReportUploadStatusLabel = (status: ReportUploadStatus) =>
  status ? reportUploadStatusLabels[status] : '-'

export const getColumns = () => [
  {
    title: 'Nr. raport',
    field: 'reportNo',
    align: 'left',
    type: 'string',
    width: '80px'
  },
  {
    title: 'Locatie',
    field: 'storeName',
    align: 'left',
    type: 'string',
    width: '150px'
  },
  {
    title: 'Data',
    field: 'creationDate',
    align: 'left',
    type: 'string',
    width: '80px'
  },
  {
    title: 'Categorii',
    field: 'wasteCategories',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    render: (row: DailyReportTableItem) => (
      <MultivalueCell value={row.wasteCategories} field="name" size={1} />
    )
  },
  {
    title: 'Destinatar',
    field: 'wasteCompanies',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    width: '150px',
    render: (row: DailyReportTableItem) => (
      <MultivalueCell value={row.wasteCompanies} field="name" size={1} />
    )
  },
  {
    title: 'Raport METI',
    field: 'metiReportIds',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    width: '180px',
    render: (row: DailyReportTableItem) => <MetiReportsCell dailyReportId={row.id} />
  },
  {
    title: 'Tip upload',
    field: 'reportUploadStatus',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    width: '100px',
    render: (row: DailyReportTableItem) => getReportUploadStatusLabel(row.reportUploadStatus)
  },
  {
    title: 'Status predare',
    field: 'isHandedOver',
    align: 'left',
    type: 'string',
    collapsible: true,
    width: '80px',
    render: (row: DailyReportTableItem) => (
      <Box style={{ display: 'flex' }}>
        <BooleanIcon
          value={row.isHandedOver}
          icon="CheckCircleOutline"
          onFailure="#E5E5E5"
          onSuccess="#33691E"
        />
      </Box>
    )
  },
  {
    title: 'Status semnatura',
    field: 'isReportSigned',
    align: 'left',
    type: 'string',
    collapsible: true,
    width: '90px',
    render: (row: DailyReportTableItem) => (
      <Box style={{ display: 'flex' }}>
        <BooleanIcon
          value={row.isReportSigned}
          icon="CheckCircleOutline"
          onFailure="#E5E5E5"
          onSuccess="#33691E"
        />
      </Box>
    )
  },
  {
    title: 'Doc',
    field: 'documentNo',
    align: 'left',
    type: 'string',
    collapsible: true,
    disableSorting: true,
    width: '80px',
    render: (row: DailyReportTableItem) => (
      <BooleanIconText
        value={row.documentNo}
        successIcon={<ApproveFileWhite />}
        failureIcon={<DeleteFileRed />}
        neutralIcon={<FileBlack />}
        isSuccess={row.isHandedOver}
        isFailure={!row.isHandedOver && row.documentNo === 0}
        isNeutral={!row.isHandedOver && row.documentNo > 0}
      />
    )
  }
]

export const getFilters = (wasteCategoryOptions: AutocompleteOption[]) => [
  {
    name: 'reportNo',
    label: 'Nr. raport',
    type: 'text',
    filter: <Input type="text" name="reportNo" label="Nr. raport" variant="outlined" value="" />
  },
  {
    name: 'startDate',
    value: null,
    filter: <DatePicker label="Data inceput" variant="outlined" value={null} onChange={() => {}} />,
    type: 'date',
    label: 'Data inceput'
  },
  {
    name: 'endDate',
    value: null,
    filter: <DatePicker label="Data sfarsit" variant="outlined" value={null} onChange={() => {}} />,
    type: 'date',
    label: 'Data sfarsit'
  },
  {
    name: 'categories',
    value: undefined,
    filter: (
      <Autocomplete
        variant="outlined"
        label="Categorii"
        options={wasteCategoryOptions || []}
        multiple
      />
    ),
    type: 'autocomplete',
    label: 'Categorii'
  },
  {
    name: 'isHandedOver',
    value: '',
    filter: (
      <Autocomplete
        variant="outlined"
        label="Status"
        options={[
          { value: 'true', label: 'Predat' },
          { value: 'false', label: 'Nepredat' }
        ]}
      />
    ),
    type: 'autocomplete',
    label: 'Status'
  },
  {
    name: 'reportUploadStatus',
    value: undefined,
    filter: (
      <Autocomplete
        variant="outlined"
        label="Tip upload"
        options={[
          { value: 'MANUAL_UPLOAD', label: 'Manual' },
          { value: 'AUTOMATIC_UPLOAD', label: 'Automat' },
          { value: 'MISSING_UPLOAD', label: 'Nefinalizat' }
        ]}
        multiple
      />
    ),
    type: 'autocomplete',
    label: 'Tip upload'
  }
]
